import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";

const REPORT_LABEL = {
  boneah: "BoneAH",
  bone_age: "Bone Age",
  growth: "Growth Assessment",
  nutrition: "NutriGuide",
};
const SUBTYPE_LABEL = {
  bone_age: "Bone Age",
  height_weight: "Height & Weight",
  nutrition: "Nutrition",
};

export default function ReportsPanel({ userRole }) {
  const [state, setState] = useState({ loading: true, scope: null, total: 0, aggregate: null, reports: [], error: "" });
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(null); // report_id being downloaded
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get("/api/reports");
      setState({
        loading: false,
        scope: data.scope,
        total: data.total,
        aggregate: data.aggregate,
        reports: data.reports || [],
        error: "",
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: formatApiError(e) }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async (rid, childName, type) => {
    setDownloading(rid);
    try {
      const resp = await api.get(`/api/reports/${rid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${REPORT_LABEL[type] || type}_Report_${childName.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (rid) => {
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    setDeleting(rid);
    try {
      await api.delete(`/api/reports/${rid}`);
      setState((s) => ({ ...s, reports: s.reports.filter((r) => r.report_id !== rid), total: s.total - 1 }));
      if (selected?.report_id === rid) setSelected(null);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setDeleting(null);
    }
  };

  if (state.loading) {
    return (
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-8 flex items-center justify-center" data-testid="reports-loading">
        <div className="w-6 h-6 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-6 text-sm text-rose-300" data-testid="reports-error">
        {state.error}
      </div>
    );
  }

  // Aggregate view (school_admin, class_teacher)
  if (state.scope === "aggregate") {
    const agg = state.aggregate || { total_reports: 0, last_30_days: 0, boneah_count: 0, nutrition_count: 0 };
    return (
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-6" data-testid="reports-aggregate">
        <p className="text-xs text-slate-500 mb-4">
          Your role can see <strong>aggregated counts only</strong> — individual children's medical data stays parent-controlled.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <AggregateCard label="Total reports" value={agg.total_reports} />
          <AggregateCard label="Last 30 days" value={agg.last_30_days} accent="sky" />
          <AggregateCard label="BoneAH" value={agg.boneah_count} />
          <AggregateCard label="NutriGuide" value={agg.nutrition_count} />
        </div>
      </div>
    );
  }

  // Empty state (self / all)
  if (state.reports.length === 0) {
    return (
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-8 text-center" data-testid="reports-empty">
        <p className="text-slate-300 font-medium">No reports yet</p>
        <p className="text-sm text-slate-500 mt-1">
          Run an analysis using BoneAH or NIN-NutriGuide — your results will appear here automatically.
        </p>
      </div>
    );
  }

  // Full list view
  return (
    <>
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 overflow-hidden" data-testid="reports-list">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <p className="text-sm text-slate-400">
            <span className="text-slate-100 font-medium">{state.total}</span>{" "}
            report{state.total === 1 ? "" : "s"} {state.scope === "all" ? "(platform-wide)" : "saved"}
          </p>
          <button
            onClick={load}
            data-testid="reports-refresh-button"
            className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
          >
            Refresh
          </button>
        </div>
        <div className="divide-y divide-slate-800/80">
          {state.reports.map((r) => (
            <ReportRow
              key={r.report_id}
              report={r}
              onView={() => setSelected(r)}
              onDownload={() => handleDownload(r.report_id, r.child_name, r.report_type)}
              onDelete={() => handleDelete(r.report_id)}
              downloading={downloading === r.report_id}
              deleting={deleting === r.report_id}
            />
          ))}
        </div>
      </div>

      {selected && (
        <ReportDetailModal report={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function AggregateCard({ label, value, accent = "slate" }) {
  const accentColor = accent === "sky" ? "text-sky-400" : "text-slate-100";
  return (
    <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-semibold mt-1 ${accentColor}`}>{value}</p>
    </div>
  );
}

function ReportRow({ report, onView, onDownload, onDelete, downloading, deleting }) {
  const COLOR_BY_TYPE = {
    boneah: "text-sky-300 bg-sky-500/10",
    bone_age: "text-sky-300 bg-sky-500/10",
    growth: "text-amber-300 bg-amber-500/10",
    nutrition: "text-emerald-300 bg-emerald-500/10",
  };
  const typeColor = COLOR_BY_TYPE[report.report_type] || "text-slate-300 bg-slate-500/10";
  const date = new Date(report.created_at).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
  });
  return (
    <div className="p-4 flex items-center justify-between gap-4 hover:bg-white/[0.02]" data-testid={`report-row-${report.report_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${typeColor}`}>
            {REPORT_LABEL[report.report_type]} · {SUBTYPE_LABEL[report.subtype] || report.subtype}
          </span>
        </div>
        <p className="text-sm font-medium text-slate-100 truncate">{report.child_name}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {date} · Age {report.child_age}{report.child_gender ? ` · ${report.child_gender}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onView}
          data-testid={`report-view-${report.report_id}`}
          className="text-xs px-2.5 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"
        >
          View
        </button>
        <button
          onClick={onDownload}
          disabled={downloading}
          data-testid={`report-pdf-${report.report_id}`}
          className="text-xs px-2.5 py-1.5 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 border border-sky-500/30 disabled:opacity-60"
        >
          {downloading ? "…" : "PDF"}
        </button>
        <button
          onClick={onDelete}
          disabled={deleting}
          data-testid={`report-delete-${report.report_id}`}
          className="text-xs px-2.5 py-1.5 rounded bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border border-rose-500/20 disabled:opacity-60"
        >
          {deleting ? "…" : "Delete"}
        </button>
      </div>
    </div>
  );
}

function ReportDetailModal({ report, onClose }) {
  // Pick a few key metrics per report type for a quick summary
  const summary = summarizeReport(report);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="report-detail-modal"
    >
      <div
        className="bg-[#0E1A3A] border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-800 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">{report.child_name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {REPORT_LABEL[report.report_type]} · {SUBTYPE_LABEL[report.subtype] || report.subtype} ·{" "}
              {new Date(report.created_at).toLocaleString()}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100 text-2xl leading-none" data-testid="report-modal-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          {summary.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {summary.map(({ label, value }) => (
                <div key={label} className="rounded-lg bg-slate-900/60 border border-slate-800 p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
                  <p className="text-sm font-medium text-slate-100 mt-1">{value}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No summary metrics available.</p>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-slate-400 hover:text-slate-200">Raw input & results (JSON)</summary>
            <pre className="mt-2 p-3 bg-slate-900/60 border border-slate-800 rounded text-slate-300 overflow-x-auto text-[11px]">
{JSON.stringify({ input: report.input_data, result: report.result_data }, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function summarizeReport(report) {
  const r = report.result_data || {};
  // Bone Age (new tag) OR legacy boneah/bone_age subtype
  if (report.report_type === "bone_age" || (report.report_type === "boneah" && report.subtype === "bone_age")) {
    return [
      { label: "Bone Age", value: r.bone_age_formatted || "—" },
      { label: "Known Age", value: `${r.known_age} yrs` },
      { label: "Difference", value: `${r.age_difference} yrs` },
      { label: "Conclusion", value: r.conclusion || "—" },
    ];
  }
  // Growth (new tag) OR legacy boneah/height_weight subtype
  if (report.report_type === "growth" || (report.report_type === "boneah" && report.subtype === "height_weight")) {
    return [
      { label: "Growth Health Index", value: `${r.growth_health_index}/100` },
      { label: "Predicted Adult Height", value: `${r.ahp_xplusp ?? r.ahp_xray} cm` },
      { label: "Weight Status", value: r.weight_status || "—" },
      { label: "Maturity", value: r.maturity_status || "—" },
    ];
  }
  if (report.report_type === "nutrition") {
    const req = r.daily_requirements || {};
    const growth = r.growth_status || {};
    return [
      { label: "Calories (daily)", value: req.calories != null ? `${req.calories} kcal` : "—" },
      { label: "Protein (daily)", value: req.protein != null ? `${req.protein} g` : "—" },
      { label: "BMI status", value: growth.bmi_status || "—" },
      { label: "Bone age diff", value: growth.bone_age_difference != null ? `${growth.bone_age_difference} yrs` : "—" },
    ];
  }
  return [];
}
